import React, { useState, useEffect, Suspense, lazy } from "react"
import { useNavigate } from "react-router-dom"
import articleApi from "../api/articleApi"
import categoriesApi from "../api/categoriesApi"
import portalsApi from "../api/portalsApi"
import adsApi from "../api/adsApi"
import customPublicationApi from "../api/customPublicationApi"
import { getTimeAgo } from "../utils/getTimeAgo"
import { stripHtmlTags } from "../utils/stripHtmlTags"
import { IoImageOutline } from "react-icons/io5"
import ImageWithFallback from "../helpers/ImageWithFallback"

// Lazy load components
const LatestNewsSection = lazy(() => import("../components/latestNews"))
const HeroSection = lazy(() => import("../components/heroSection"))
const ArticlesByCategory = lazy(() => import("../components/articlesByCategory"))
const CoursesSection = lazy(() => import("../components/coursesSection"))

const AdComponent = ({ ad }) => {
  if (!ad || !ad.is_active) return null

  return (
    <div className="w-full h-40 bg-white dark:bg-gray-800 rounded-lg shadow-sm my-8">
      <a
        href={ad.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full h-full"
      >
        <img
          src={ad.image}
          alt={ad.title}
          className="w-full h-full object-contain"
        />
      </a>
    </div>
  )
}

const VerticalAdComponent = ({ ad }) => {
  if (!ad || !ad.is_active) return null

  return (
    <div className="hidden lg:block w-full h-[280px] bg-white dark:bg-gray-800 rounded-lg shadow-sm my-8">
      <a
        href={ad.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full h-full"
      >
        <img
          src={ad.image}
          alt={ad.title}
          className="w-full h-full object-contain"
        />
      </a>
    </div>
  )
}

const LatestPublication = ({ publication, error }) => {
  const navigate = useNavigate()

  if (error) {
    return (
      <div>
        <strong></strong>
        <span></span>
      </div>
    )
  }

  if (!publication) {
    return null
  }

  const thumbnailUrl = publication.thumbnail?.startsWith("http")
    ? publication.thumbnail
    : `${process.env.REACT_APP_API_URL}${publication.thumbnail}`

  const handlePublicationClick = (e) => {
    // Only prevent default if it's a left click (not right click or middle click)
    if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      navigate(`/publication/${publication.id}`)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <a
      href={`/publication/${publication.id}`}
      className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-6 cursor-pointer hover:shadow-md transition-shadow duration-200"
      onClick={handlePublicationClick}
    >
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        Nga ekipi Merrvesh
      </h3>
      <div className="p-4">
        <div className="relative w-full h-40 mb-3">
          <ImageWithFallback
            src={thumbnailUrl}
            className="w-full h-full object-cover rounded"
            fallback={
              <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center rounded">
                <IoImageOutline className="w-12 h-12 text-gray-400 dark:text-gray-500" />
              </div>
            }
          />
        </div>
        <h4 className="text-md font-bold text-gray-900 dark:text-white mb-1 line-clamp-2">
          {publication.title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-3">
          {stripHtmlTags(publication.content)}
        </p>
        <span className="text-xs text-gray-500 dark:text-gray-500">
          {getTimeAgo(publication.created_at)}
        </span>
      </div>
    </a>
  )
}

// Loading skeleton components
const CategorySkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-3xl border-b-2 border-gray-800 pb-2 font-bold text-gray-900 flex items-center">
        &nbsp;
      </h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 items-start">
      <div className="col-span-1 md:col-span-2 lg:col-span-2">
        <div className="w-full h-64 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-3/4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
      <div className="col-span-1 md:col-span-1 lg:col-span-3">
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-start space-x-3">
              <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const VideoSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-3xl border-b-2 border-gray-800 pb-2 font-bold text-gray-900">
        VIDEO
      </h2>
    </div>
    <div className="w-full h-0.5 mb-6 bg-gray-200 dark:bg-gray-700"></div>
    <div className="flex flex-col lg:flex-row bg-[#323232] text-black p-4 rounded-lg" style={{ minHeight: '500px' }}>
      {/* Main Video Section Skeleton */}
      <div className="flex-1 lg:mr-4 flex flex-col mb-4 lg:mb-0">
        <div className="flex-grow bg-[#424242] p-4 rounded-lg shadow-lg flex flex-col justify-center items-center">
          <div className="w-full h-64 lg:h-80 bg-gray-500 rounded-md mb-4"></div>
          <div className="w-3/4 h-6 bg-gray-400 rounded mb-2"></div>
          <div className="flex gap-3 w-3/4">
            <div className="flex items-center gap-2 w-1/2">
              <div className="w-6 h-6 bg-gray-400 rounded-full"></div>
              <div className="h-4 w-20 bg-gray-400 rounded"></div>
            </div>
            <div className="h-4 w-16 bg-gray-400 rounded"></div>
            <div className="h-4 w-16 bg-gray-400 rounded"></div>
          </div>
        </div>
      </div>
      {/* Sidebar Videos Skeleton */}
      <div className="lg:w-1/3 w-full flex flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-[#707070] scrollbar-track-[#323232]" style={{ maxHeight: '500px' }}>
        <ul className="space-y-4">
          {[...Array(7)].map((_, i) => (
            <li key={i} className="flex items-start space-x-3 p-2 rounded-lg bg-[#424242]">
              <div className="w-24 h-14 bg-gray-500 rounded-md flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-400 rounded mb-1 w-3/4"></div>
                <div className="flex items-center gap-1 mb-1">
                  <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                  <div className="h-3 w-16 bg-gray-400 rounded"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-3 w-12 bg-gray-400 rounded"></div>
                  <div className="h-3 w-12 bg-gray-400 rounded"></div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
    {/* More Videos Button Skeleton */}
    <div className="flex justify-center mt-6 mb-4">
      <div className="inline-flex items-center justify-center px-8 py-3 bg-gray-300 rounded-lg">
        <div className="w-24 h-4 bg-gray-400 rounded mr-2"></div>
        <div className="w-4 h-4 bg-gray-400 rounded"></div>
      </div>
    </div>
  </div>
)

const NewsSkeleton = () => (
  <div className="space-y-6">
    {[...Array(2)].map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl border-b-2 border-gray-800 pb-2 font-bold text-gray-900">
            Lajmet e Fundit
          </h2>
        </div>
        <div className="w-full h-0.5 mb-6 bg-gray-200 dark:bg-gray-700"></div>
        {/* Top 2 Large Articles Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="flex flex-col h-full">
              <div className="w-full h-40 sm:h-48 md:h-56 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div className="mt-2 flex-1 flex items-start">
                <div className="min-w-[4px] w-1 h-4 bg-gray-300 dark:bg-gray-600 mr-2 flex-shrink-0 mt-0"></div>
                <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div className="mt-2 flex justify-between items-center text-xs text-gray-400">
                <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Remaining Articles Skeleton (List View) */}
        <div className="mt-6 space-y-4">
          {[...Array(5)].map((_, k) => (
            <div key={k} className="flex items-start space-x-3 pb-4 border-b border-gray-200">
              <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start">
                  <div className="min-w-[3px] w-1 h-3 bg-gray-300 dark:bg-gray-600 mr-2 flex-shrink-0 mt-1"></div>
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                </div>
                <div className="mt-1 mr-1 flex justify-between items-center text-xs text-gray-400">
                  <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-3 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

const PublicationSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-6 animate-pulse">
    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
    </div>
    <div className="p-4">
      <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-4/5 mb-2"></div>
      <div className="space-y-2 mb-2">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
      </div>
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
    </div>
  </div>
)

const HomePage = () => {
  const [heroArticles, setHeroArticles] = useState([])
  const [latestNewsArticles, setLatestNewsArticles] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [portals, setPortals] = useState([])
  const [isLoadingArticlesByCategory, setIsLoadingArticlesByCategory] =
    useState(true)
  const [articlesByCategory, setArticlesByCategory] = useState({})
  const [categories, setCategories] = useState([])
  const [ads, setAds] = useState({})
  const [adsError, setAdsError] = useState(null)
  const [latestPublication, setLatestPublication] = useState(null)
  const [latestPublicationError, setLatestPublicationError] = useState(null)
  
  // Loading states for individual sections
  const [isLoadingNews, setIsLoadingNews] = useState(true)
  const [isLoadingPublication, setIsLoadingPublication] = useState(true)

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await adsApi.getAll()

        if (response.data.status === "success") {
          const processedAds = {}
          Object.entries(response.data.data).forEach(([key, adList]) => {
            processedAds[key] = adList.map(ad => ({
              ...ad,
              image: ad.image.startsWith("http")
                ? ad.image
                : `${process.env.REACT_APP_API_URL}${ad.image}`,
            }))
          })
          setAds(processedAds)
        } else {
          setAdsError(
            "Failed to load ads: " + (response.data.message || "Unknown error")
          )
        }
      } catch (err) {
        setAdsError("Error fetching ads: " + err.message)
        console.error("Error fetching ads:", err)
      }
    }
    fetchAds()
  }, [])

  useEffect(() => {
    const fetchTopViewedArticles = async () => {
      try {
        const response = await articleApi.getTopViewed()
        const topViewedArticles = response.data || []
        if (topViewedArticles.length > 0) {
          setHeroArticles(topViewedArticles.slice(1))
        }
      } catch (err) {
        console.error("Error fetching top-viewed articles:", err)
      }
    }
    fetchTopViewedArticles()
  }, [])

  useEffect(() => {
    const fetchLatestNewsArticles = async () => {
      setIsLoadingNews(true)
      setError(null)

      try {
        const { data } = await articleApi.getAll({ page_size: 50 })

        if (!data || !data.results || data.results.length === 0) {
          setLatestNewsArticles([])
          throw new Error("No articles found")
        }

        const sortedArticles = [...data.results].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )

        if (sortedArticles.length > 0) {
          const remainingArticles = sortedArticles.slice(1, 8)
          setLatestNewsArticles(remainingArticles)
        }
      } catch (err) {
        console.error("Error fetching latest news articles:", err)
        setError("Failed to load latest news articles. Please try again later.")
      } finally {
        setIsLoadingNews(false)
        setIsLoading(false)
      }
    }

    fetchLatestNewsArticles()
  }, [])

  useEffect(() => {
    const fetchPortals = async () => {
      try {
        const { data } = await portalsApi.getAll()
        setPortals(data)
      } catch (err) {
        console.error("Error fetching portals:", err)
        setError(err.message)
      }
    }
    fetchPortals()
  }, [])

  useEffect(() => {
    const fetchCategoriesAndArticles = async () => {
      setIsLoadingArticlesByCategory(true)
      setError(null)

      try {
        const { data: fetchedCategoriesWithArticles } = await categoriesApi.getAll({ embed_articles: true })
        
        const categoriesOnly = fetchedCategoriesWithArticles.map(({ top_articles, ...category }) => category)
        setCategories(categoriesOnly)

        const articlesByCategoryObject = {}
        fetchedCategoriesWithArticles.forEach(category => {
          if (category.top_articles && category.top_articles.length > 0) {
            articlesByCategoryObject[category.id] = category.top_articles
          }
        })
        
        setArticlesByCategory(articlesByCategoryObject)

      } catch (err) {
        console.error(
          "Error fetching categories with embedded articles:",
          err
        )
        setError("Failed to load category data. Please try again later.")
        setArticlesByCategory({}) 
        setCategories([]) 
      } finally {
        setIsLoadingArticlesByCategory(false)
      }
    }

    fetchCategoriesAndArticles()
  }, [])

  useEffect(() => {
    const fetchLatestPublication = async () => {
      setIsLoadingPublication(true)
      setLatestPublicationError(null)
      try {
        const response = await customPublicationApi.getLatest()
        if (response.data) {
          setLatestPublication(response.data)
        } else {
          throw new Error("No data received for latest publication")
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail || err.message || "Unknown error occurred"
        setLatestPublicationError(`Failed to load: ${errorMessage}`)
      } finally {
        setIsLoadingPublication(false)
      }
    }

    fetchLatestPublication()
  }, [])

  if (isLoading) {
    return (
      <main className="min-h-screen bg-gray-50 dark:bg-gray-900 sm:pt-6">
        <div className="container mx-auto px-0 sm:px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <VideoSkeleton />
            </div>
            <div className="space-y-6 px-0 sm:px-0">
              <PublicationSkeleton />
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
            <div className="lg:col-span-3 space-y-6">
              <NewsSkeleton />
            </div>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-gray-50 dark:bg-gray-900 sm:pt-6">
      <div className="container mx-auto px-0 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            {/* Categories Section */}
            {isLoadingArticlesByCategory ? (
              <>
                <CategorySkeleton />
                <CategorySkeleton />
                <CategorySkeleton />
              </>
            ) : (
              <Suspense fallback={<CategorySkeleton />}>
                <ArticlesByCategory
                  groupedArticles={articlesByCategory}
                  categories={categories}
                  portals={portals}
                  ads={ads}
                  latestPublication={latestPublication}
                  isLoadingPublication={isLoadingPublication}
                  latestPublicationError={latestPublicationError}
                />
              </Suspense>
            )}
            
            {/* Video Section */}
            {adsError ? (
              <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {adsError}
              </div>
            ) : (
              <>
                <AdComponent ad={ads.home_before_latest_news?.[0]} />
              </>
            )}
          </div>

          <div className="space-y-6 px-0 sm:px-0">
            {/* Hide publication on mobile since it's shown after first category */}
            <div className="hidden lg:block">
              {isLoadingPublication ? (
                <PublicationSkeleton />
              ) : (
                <LatestPublication publication={latestPublication} error={latestPublicationError} />
              )}
            </div>

            {/* Move HeroSection above the ads */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <Suspense fallback={
                <div className="p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                </div>
              }>
                <HeroSection
                  articles={heroArticles}
                  portals={portals}
                  categories={categories}
                  ads={ads}
                />
              </Suspense>
            </div>

            {/* Right sidebar ads below HeroSection */}
            {adsError ? (
              <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {adsError}
              </div>
            ) : (
              <>
                <VerticalAdComponent ad={ads.home_right_1?.[0]} />
                <VerticalAdComponent ad={ads.home_right_2?.[0]} />
                <VerticalAdComponent ad={ads.home_right_3?.[0]} />
                <VerticalAdComponent ad={ads.home_right_4?.[0]} />
                <VerticalAdComponent ad={ads.home_right_5?.[0]} />
                <VerticalAdComponent ad={ads.home_right_6?.[0]} />
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            {adsError ? (
              <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {adsError}
              </div>
            ) : (
              <>
                <AdComponent ad={ads.home_before_latest_news?.[0]} />
                {!error && (
                  isLoadingNews ? (
                    <NewsSkeleton />
                  ) : (
                    <Suspense fallback={<NewsSkeleton />}>
                      <LatestNewsSection
                        articles={latestNewsArticles}
                        articlesViews={heroArticles}
                      />
                    </Suspense>
                  )
                )}
                <AdComponent ad={ads.home_before_courses?.[0]} />
              </>
            )}
          </div>
          <div className="space-y-6 px-0 sm:px-0">
            {/* Right sidebar space for consistency */}
          </div>
        </div>

        <div className="px-0 sm:px-0">
          <Suspense fallback={
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            </div>
          }>
            <CoursesSection />
          </Suspense>
        </div>
      </div>
    </main>
  )
}

export default HomePage
